import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Lock, Activity, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import ConnectorIcon from "./ConnectorIcon";
import { CENTER_GROUPS } from "./adminCenters";
import AdminHealthStatus from "./AdminHealthStatus";

export default function AdminShell({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/login"); };

  return <div className="min-h-screen overflow-x-hidden bg-slate-100 text-slate-900">
    <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 flex-col border-r border-white/10 bg-[#08111f] xl:flex">
      <div className="flex h-20 items-center border-b border-slate-200 bg-white px-5">
        <img src="/cleaniq-logo-compact.webp" alt="CleanIQ — Clean Data. Smarter CRM." className="h-auto w-52 max-w-full object-contain" />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {CENTER_GROUPS.map((group) => <div key={group.label} className="mb-5">
          <div className="mb-2 px-3 text-[9px] font-semibold uppercase tracking-[0.22em] text-slate-600">{group.label}</div>
          {group.items.map((item) => {
            const active = location.pathname === item.to;
            return <Link key={item.key} to={item.to} data-testid={"admin-nav-"+item.key}
              className={"mb-1 flex items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-sm transition-all " +
                (active ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-600/15" : "text-slate-400 hover:bg-white/[0.06] hover:text-white")}>
              <span className="flex items-center gap-3"><ConnectorIcon name={item.icon} className="h-4 w-4" />{item.name}</span>
              {!item.built && <Lock className="h-3 w-3 text-slate-600" />}
            </Link>;
          })}
        </div>)}
      </nav>
      <div className="border-t border-white/10 p-4">
        <Link to="/dashboard" data-testid="admin-customer-view" className="mb-3 flex items-center gap-2 text-xs text-slate-400 hover:text-white"><ArrowLeft className="h-3.5 w-3.5" /> Customer workspace</Link>
        <div className="flex items-center gap-3">
          <img src="/cleaniq-data-flow.jpg" alt="" aria-hidden="true" className="h-12 w-12 shrink-0 rounded-xl object-cover ring-1 ring-white/15" />
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-300">CleanIQ administrator</div>
            <div className="truncate text-xs font-medium text-slate-300">{user?.email}</div>
          </div>
        </div>
        <button data-testid="admin-logout-button" onClick={doLogout} className="mt-3 flex w-full items-center gap-2 rounded-lg border border-white/10 px-3 py-2 text-xs text-slate-400 hover:bg-white/5 hover:text-white"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
      </div>
    </aside>

    <div className="min-w-0 bg-slate-100 text-slate-900 xl:pl-72">
      <header className="sticky top-0 z-20 flex min-h-20 items-center justify-between border-b border-slate-200 bg-white/95 px-4 py-4 shadow-sm backdrop-blur-xl sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <img src="/favicon-32.png" alt="CleanIQ" className="h-9 w-9 shrink-0 rounded-lg bg-white object-contain p-1 xl:hidden" />
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-blue-600"><Activity className="h-3.5 w-3.5" /> Live operations</div>
            <h1 className="truncate font-heading text-2xl font-bold tracking-tight text-slate-950">{title}</h1>
            {subtitle && <p className="mt-1 truncate text-sm text-slate-600">{subtitle}</p>}
          </div>
        </div>
        <div className="ml-4 flex shrink-0 items-center gap-2">
          <AdminHealthStatus />
          <button data-testid="admin-mobile-logout-button" aria-label="Sign out" onClick={doLogout}
            className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 xl:hidden">
            <LogOut className="h-4 w-4" /><span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>
      <div className="border-b border-slate-200 bg-white px-3 py-2 xl:hidden">
        <nav className="flex gap-1 overflow-x-auto">
          {CENTER_GROUPS.flatMap(g=>g.items).filter(i=>i.built).map(item=><Link key={item.key} to={item.to}
            className={"shrink-0 rounded-lg px-3 py-2 text-xs font-medium "+(location.pathname===item.to?"bg-blue-600 text-white":"text-slate-600 hover:bg-slate-100")}>{item.name}</Link>)}
        </nav>
      </div>
      <main className="min-w-0 p-4 sm:p-6 lg:p-8"><div className="mx-auto max-w-[1600px]">{children}</div></main>
    </div>
  </div>;
}
